import type { ReactNode } from 'react';
import { Box, Center, Container, Group, Menu, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserResponse } from '@/api/generated/models';
import {
  getListUsersQueryKey,
  getSearchUsersQueryKey,
  useDeactivateUser,
  useReactivateUser,
  useSearchUsers,
  useSendPasswordReset,
  useUpdateRole
} from '@/api/generated/users/users';
import { IconFigmaInfo } from '@/assets/icons';
import {
  ContentCard,
  KebabTrigger,
  ListPagination,
  PageHeading,
  SearchField,
  StatusPill,
  tableRowStyle,
  Toast
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { AccessRestrictedCard } from '@/features/users/components/AccessRestrictedCard';
import { ChangeRoleModal } from '@/features/users/components/ChangeRoleModal';
import { DeactivateUserModal } from '@/features/users/components/DeactivateUserModal';
import { SendResetEmailModal } from '@/features/users/components/SendResetEmailModal';
import { formatDateTimeParts } from '@/utils/format';
import { usePageSize } from '@/utils/usePageSize';

const initials = (firstName?: string, lastName?: string) =>
  `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase();

const cellStyle = { color: 'var(--color-text-secondary)' };

const pillStyle = (bg: string, fg: string) => ({
  borderRadius: 999,
  width: 'fit-content' as const,
  backgroundColor: bg,
  color: fg
});

const RoleBadge = ({ role }: { role?: string }) => {
  const { t } = useTranslation();
  const isAdmin = role === 'ORGANISATION_ADMIN' || role === 'APP_ADMIN';

  return (
    <Group
      gap={4}
      wrap="nowrap"
      px={12}
      py={4}
      style={pillStyle(
        isAdmin ? 'var(--color-accent-primary-soft)' : 'var(--color-role-user-soft)',
        isAdmin ? 'var(--color-accent-primary-active)' : 'var(--color-role-user)'
      )}
    >
      <Text fz={11} fw={600} style={{ color: 'inherit' }}>
        {isAdmin ? t('users.invite.roleAdmin') : t('users.invite.roleUser')}
      </Text>
    </Group>
  );
};

const StatusBadge = ({ active }: { active: boolean }) => {
  const { t } = useTranslation();

  return (
    <StatusPill
      label={active ? t('users.roles.statusActive') : t('users.roles.statusDeactivated')}
      bg={active ? 'var(--color-status-success-soft)' : 'var(--color-status-danger-soft)'}
      fg={active ? 'var(--color-status-success)' : 'var(--color-status-danger)'}
    />
  );
};

const MetaRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Group justify="space-between" wrap="nowrap">
    <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
      {label}
    </Text>
    {children}
  </Group>
);

export const RolesDeactivationPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthContext();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [roleChangeUser, setRoleChangeUser] = useState<UserResponse | null>(null);
  const [deactivateUser, setDeactivateUser] = useState<UserResponse | null>(null);
  const [sendResetUser, setSendResetUser] = useState<UserResponse | null>(null);
  const [resetFeedback, setResetFeedback] = useState<{
    email: string;
    tone: 'success' | 'danger';
  } | null>(null);

  const isAdmin = currentUser?.role === 'ORGANISATION_ADMIN' || currentUser?.role === 'APP_ADMIN';

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  // staleTime: 0 — status/uloga se može promijeniti iz druge sesije bez
  // signala natrag ovoj stranici (isti razlog kao Partner Linking).
  const { data, isLoading } = useSearchUsers(
    { page, pageSize, search: debouncedSearch.trim() || undefined },
    { query: { enabled: isAdmin, staleTime: 0 } }
  );
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // refetchType: 'all' — forsira ponovni dohvat odmah, ne samo označi upit
  // zastarjelim (default 'active' bi trebao pokriti isto, ali ovo je
  // eksplicitno jamstvo bez obzira na suptilne edge-caseove "aktivnog" upita).
  // Invalidira i stari /users (koriste ga pickeri/dropdovi drugdje) i novi /users/search.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListUsersQueryKey(), refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: getSearchUsersQueryKey(), refetchType: 'all' });
  };

  const updateRoleMutation = useUpdateRole({ mutation: { onSuccess: invalidate } });
  const deactivateMutation = useDeactivateUser({ mutation: { onSuccess: invalidate } });
  const reactivateMutation = useReactivateUser({ mutation: { onSuccess: invalidate } });
  const sendResetMutation = useSendPasswordReset({
    mutation: { meta: { skipGlobalErrorToast: true }, onSuccess: invalidate }
  });

  const handleConfirmRoleChange = (userId: string, role: 'USER' | 'ORGANISATION_ADMIN') => {
    updateRoleMutation.mutate(
      { id: userId, data: { role } },
      { onSuccess: () => setRoleChangeUser(null) }
    );
  };

  const handleConfirmDeactivate = (userId: string) => {
    deactivateMutation.mutate({ id: userId }, { onSuccess: () => setDeactivateUser(null) });
  };

  const handleReactivate = (userId: string) => {
    reactivateMutation.mutate({ id: userId });
  };

  const handleConfirmSendReset = (userId: string) => {
    const email = sendResetUser?.email ?? '';
    setResetFeedback(null);
    sendResetMutation.mutate(
      { id: userId },
      {
        onSuccess: () => {
          setSendResetUser(null);
          setResetFeedback({ email, tone: 'success' });
        },
        onError: () => {
          setSendResetUser(null);
          setResetFeedback({ email, tone: 'danger' });
        }
      }
    );
  };

  const actionsMenu = (user: UserResponse) => {
    // Nema smisla mijenjati vlastitu ulogu, deaktivirati sam sebe ili si
    // poslati reset lozinke iz ovog admin popisa — sakrij akcije na vlastitom retku.
    if (user.id === currentUser?.id) {
      return null;
    }

    return (
      <Menu
        opened={menuOpenId === user.id}
        onChange={(opened) => setMenuOpenId(opened ? (user.id ?? null) : null)}
        position="bottom-end"
        shadow="md"
        radius="md"
        width={200}
      >
        <Menu.Target>
          <KebabTrigger />
        </Menu.Target>
        <Menu.Dropdown>
          {user.isActive && (
            <Menu.Item
              styles={{ item: { color: 'var(--color-text-primary)' } }}
              onClick={() => setRoleChangeUser(user)}
            >
              {t('users.roles.menuChangeRole')}
            </Menu.Item>
          )}
          {user.isActive ? (
            <Menu.Item
              styles={{ item: { color: 'var(--color-status-danger)' } }}
              onClick={() => setDeactivateUser(user)}
            >
              {t('users.roles.menuDeactivate')}
            </Menu.Item>
          ) : (
            <Menu.Item
              styles={{ item: { color: 'var(--color-status-success)' } }}
              onClick={() => user.id && handleReactivate(user.id)}
            >
              {t('users.roles.menuReactivate')}
            </Menu.Item>
          )}
          {user.isActive && (
            <Menu.Item
              styles={{ item: { color: 'var(--color-text-primary)' } }}
              onClick={() => setSendResetUser(user)}
            >
              {t('users.roles.menuSendReset')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };

  const paginationLabel = t('users.roles.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  if (!isAdmin) {
    return (
      <Container size="lg" px={0}>
        <AccessRestrictedCard />
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <PageHeading title={t('users.roles.title')} subtitle={t('users.roles.subtitle')} />

        <Box w={{ base: '100%', sm: 320 }}>
          <SearchField
            hideLabel
            placeholder={t('users.roles.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Box>

        {resetFeedback && (
          <Toast tone={resetFeedback.tone}>
            {resetFeedback.tone === 'success'
              ? t('users.roles.sendResetSuccess', { email: resetFeedback.email })
              : t('users.roles.sendResetError', { email: resetFeedback.email })}
          </Toast>
        )}

        {/* Desktop: single bordered card with a table */}
        <ContentCard visibleFrom="sm">
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.roles.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                {[
                  ['users.roles.colUser', 260],
                  ['users.roles.colRole', 130],
                  ['users.roles.colLastLogin', 180],
                  ['users.roles.colStatus', 130]
                ].map(([key, width]) => (
                  <Text
                    key={key as string}
                    fz={10}
                    fw={600}
                    w={width}
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {t(key as string)}
                  </Text>
                ))}
                <Text
                  fz={10}
                  fw={600}
                  w={90}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('users.roles.colActions')}
                </Text>
              </div>

              {pageItems.map((user, index) => {
                const lastLogin = user.lastLoginAt ? formatDateTimeParts(user.lastLoginAt) : null;

                return (
                  <div
                    key={user.id}
                    style={{
                      ...tableRowStyle,
                      padding: '13px 20px',
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                      opacity: user.isActive ? 1 : 0.7
                    }}
                  >
                    <Group gap={10} wrap="nowrap" w={260}>
                      <Center
                        w={28}
                        h={28}
                        style={{
                          borderRadius: 999,
                          backgroundColor: 'var(--color-bg-surface-2)',
                          border: '1px solid var(--color-border-default)',
                          flexShrink: 0
                        }}
                      >
                        <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                          {initials(user.firstName, user.lastName)}
                        </Text>
                      </Center>
                      <Stack gap={0}>
                        <Text fz={12.5} style={{ color: 'var(--color-text-primary)' }}>
                          {user.firstName} {user.lastName}
                        </Text>
                        <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                          {user.email}
                        </Text>
                      </Stack>
                    </Group>

                    <div style={{ width: 130 }}>
                      <RoleBadge role={user.role} />
                    </div>

                    <div style={{ width: 180 }}>
                      {lastLogin ? (
                        <Stack gap={0}>
                          <Text fz={10.5} style={cellStyle}>
                            {lastLogin.date}
                          </Text>
                          <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                            {lastLogin.time}
                          </Text>
                        </Stack>
                      ) : (
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('users.roles.neverLoggedIn')}
                        </Text>
                      )}
                    </div>

                    <div style={{ width: 130 }}>
                      <StatusBadge active={!!user.isActive} />
                    </div>

                    <Group w={90} justify="flex-end">
                      {actionsMenu(user)}
                    </Group>
                  </div>
                );
              })}

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </ContentCard>

        {/* Mobile: individually-bordered cards, standalone pagination */}
        <Stack hiddenFrom="sm" gap={12} w="100%">
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.roles.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.map((user) => {
            const lastLogin = user.lastLoginAt ? formatDateTimeParts(user.lastLoginAt) : null;

            return (
              <ContentCard key={user.id} style={{ padding: 16, opacity: user.isActive ? 1 : 0.7 }}>
                <Stack gap={14}>
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Group gap={12} wrap="nowrap">
                      <Center
                        w={28}
                        h={28}
                        style={{
                          borderRadius: 999,
                          backgroundColor: 'var(--color-bg-surface-2)',
                          border: '1px solid var(--color-border-default)',
                          flexShrink: 0
                        }}
                      >
                        <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                          {initials(user.firstName, user.lastName)}
                        </Text>
                      </Center>
                      <Stack gap={2}>
                        <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                          {user.firstName} {user.lastName}
                        </Text>
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {user.email}
                        </Text>
                      </Stack>
                    </Group>
                    {actionsMenu(user)}
                  </Group>

                  <Box style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />

                  <Stack gap={8}>
                    <MetaRow label={t('users.roles.colRole')}>
                      <RoleBadge role={user.role} />
                    </MetaRow>
                    <MetaRow label={t('users.roles.colStatus')}>
                      <StatusBadge active={!!user.isActive} />
                    </MetaRow>
                    <MetaRow label={t('users.roles.colLastLogin')}>
                      <Text fz={11} style={cellStyle}>
                        {lastLogin
                          ? `${lastLogin.date} ${lastLogin.time}`
                          : t('users.roles.neverLoggedIn')}
                      </Text>
                    </MetaRow>
                  </Stack>
                </Stack>
              </ContentCard>
            );
          })}

          {pageItems.length > 0 && (
            <ListPagination
              label={paginationLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              bordered={false}
            />
          )}
        </Stack>

        <Group
          gap={12}
          wrap="nowrap"
          align="flex-start"
          p={16}
          style={{ borderRadius: 12, backgroundColor: 'var(--color-accent-secondary-soft)' }}
        >
          <div style={{ flexShrink: 0 }}>
            <IconFigmaInfo size={24} color="var(--color-text-primary)" />
          </div>
          <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
            {t('users.roles.infoBanner')}
          </Text>
        </Group>
      </Stack>

      <ChangeRoleModal
        user={roleChangeUser}
        onClose={() => setRoleChangeUser(null)}
        onConfirm={handleConfirmRoleChange}
        loading={updateRoleMutation.isPending}
        isPaused={updateRoleMutation.isPaused}
      />

      <DeactivateUserModal
        user={deactivateUser}
        onClose={() => setDeactivateUser(null)}
        onConfirm={handleConfirmDeactivate}
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
      />

      <SendResetEmailModal
        user={sendResetUser}
        onClose={() => setSendResetUser(null)}
        onConfirm={handleConfirmSendReset}
        loading={sendResetMutation.isPending}
        isPaused={sendResetMutation.isPaused}
      />
    </Container>
  );
};
